/**
 * Resolves which SMS body to send for a touchpoint, swapping to a timeless
 * check-in when the holiday freshness window has expired.
 */

const HOLIDAY_ANCHOR_PATTERNS = [
  /\bmemorial day\b/i,
  /\bvictoria day\b/i,
  /\b(canada day|4th of july|independence day|july 4(th)?)\b/i,
  /\b(happy|hope (your|the)) 4th\b/i,
  /\b(the|your) 4th\b/i,
  /\b(labou?r day)\b/i,
  /\b(thanksgiving)\b/i,
  /\b(halloween)\b/i,
  /\b(easter|good friday)\b/i,
  /\b(st\.? patrick'?s)\b/i,
  /\b(valentine'?s)\b/i,
  /\b(remembrance day)\b/i,
  /\b(family day|presidents'? day)\b/i,
  /\b(civic holiday)\b/i,
  /\blong weekend\b/i,
  /\bnew year('s| eve)?\b/i,
  /\bhappy new year\b/i,
  /\b(merry christmas|happy holidays)\b/i,
  /\bhappy (halloween|easter|thanksgiving|memorial|valentine|christmas)\b/i,
  /\bfireworks\b/i,
];

/** Weak retrospective phrasing that reads like scheduled marketing. */
export const STALE_RETROSPECTIVE_PATTERNS = [
  /\bjust passed\b/i,
  /\bjust wrapped\b/i,
  /\balready behind us\b/i,
  /\bweekend (is|was) done\b/i,
  /\blong weekend just\b/i,
  // Month-is-over phrasing — reads as nonsense when sent early in the month
  /\b(?:flew|flown|flies|flying) by\b/i,
  /\b(?:went|going|gone|goes) by (?:so |too )?(?:fast|quick(?:ly)?)\b/i,
  /\b(?:almost|already|nearly) (?:over|done|gone|wrapped)\b/i,
  /\bcoming to an end\b/i,
];

/**
 * @param {string} messageText
 * @returns {boolean}
 */
export function hasHolidayAnchor(messageText) {
  const body = stripAgentSuffix(messageText);
  return HOLIDAY_ANCHOR_PATTERNS.some((p) => p.test(body));
}

/**
 * Align stored flag with message text (holiday cues in text always win).
 * @param {string} messageText
 * @param {boolean|null|undefined} isHolidayMessage
 * @returns {boolean}
 */
export function reconcileHolidayMessageFlag(messageText, isHolidayMessage = null) {
  if (hasHolidayAnchor(messageText)) return true;
  if (isHolidayMessage === true) return true;
  return false;
}

/**
 * Whether sends/UI should treat this as a holiday-anchored touchpoint.
 * @param {string} messageText
 * @param {boolean|null|undefined} storedIsHolidayMessage
 * @returns {boolean}
 */
export function resolveIsHolidayMessage(messageText, storedIsHolidayMessage = null) {
  return reconcileHolidayMessageFlag(messageText, storedIsHolidayMessage);
}

/**
 * @param {string} messageText
 * @returns {boolean}
 */
export function hasStaleRetrospectiveTone(messageText) {
  const body = stripAgentSuffix(messageText);
  return STALE_RETROSPECTIVE_PATTERNS.some((p) => p.test(body));
}

function stripAgentSuffix(messageText) {
  return (messageText || '').replace(/\s+-\s+[A-Z].+$/, '').trim();
}

/**
 * Timeless check-ins when a holiday hook is no longer timely.
 * @param {number} monthNum 1–12
 * @param {string} agentName
 * @returns {string}
 */
export function getTimelessFallbackMessage(monthNum, agentName) {
  const options = {
    1: ['How\'ve you been? Hope the year\'s been good', 'Thinking of you, hope January\'s been good'],
    2: ['How\'ve you been? Hope February\'s treating you well', 'Thinking of you, hope all\'s well'],
    3: ['How\'ve you been? Hope spring\'s treating you well', 'Thinking of you, hope March\'s been good'],
    4: ['How\'ve you been? Hope spring\'s been good for you', 'Thinking of you, hope April\'s been great'],
    5: ['How\'ve you been? Hope May\'s been good to you', 'Thinking of you, hope all\'s well'],
    6: ['How\'ve you been? Hope you\'re enjoying the longer days', 'Thinking of you, hope June\'s been great'],
    7: ['How\'ve you been? Hope summer\'s treating you well', 'Thinking of you, hope July\'s been good'],
    8: ['How\'ve you been? Hope you\'re soaking up summer', 'Thinking of you, hope August\'s been great'],
    9: ['How\'ve you been? Hope fall\'s treating you well', 'Thinking of you, hope September\'s been good'],
    10: ['How\'ve you been? Hope fall\'s treating you well', 'Thinking of you, hope October\'s been great'],
    11: ['How\'ve you been? Hope November\'s been good', 'Thinking of you, hope all\'s well'],
    12: ['How\'ve you been? Hope the season\'s been good', 'Thinking of you, hope December\'s treating you well'],
  };
  const pool = options[monthNum] || options[5];
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Pick the SMS body to send today.
 * @param {Object} params
 * @param {string} params.messageText - Stored touchpoint message
 * @param {string} params.monthYear - YYYY-MM
 * @param {string} params.agentName
 * @param {string|null} params.sendBeforeDate - YYYY-MM-DD
 * @param {string} params.todayStr - YYYY-MM-DD (agent-local or UTC date)
 * @returns {{ text: string, usedFallback: boolean, reason: string|null }}
 */
export function resolveMessageForSend({
  messageText,
  monthYear,
  agentName,
  sendBeforeDate = null,
  todayStr,
}) {
  const monthNum = parseInt(monthYear.split('-')[1], 10);
  const pastFreshnessWindow =
    sendBeforeDate && todayStr && todayStr > sendBeforeDate;

  if (pastFreshnessWindow && hasHolidayAnchor(messageText)) {
    return {
      text: getTimelessFallbackMessage(monthNum, agentName),
      usedFallback: true,
      reason: `holiday_anchor_after_${sendBeforeDate}`,
    };
  }

  if (hasStaleRetrospectiveTone(messageText)) {
    return {
      text: getTimelessFallbackMessage(monthNum, agentName),
      usedFallback: true,
      reason: 'stale_retrospective_tone',
    };
  }

  return { text: messageText, usedFallback: false, reason: null };
}

/**
 * When to send and how to spread quota.
 * - Holiday-specific copy: entire month's sends happen only in the ~7-day window after the holiday,
 *   split evenly across that window (per agent SMS schedule).
 * - Non-holiday copy: spread evenly across the full calendar month.
 *
 * @returns {{
 *   canSendToday: boolean,
 *   phase: 'waiting'|'holiday_window'|'window_closed'|'full_month',
 *   quotaSendAfter: string|null,
 *   quotaSendBefore: string|null,
 *   quotaLockToFullWindow: boolean,
 *   usesHolidayWindow: boolean,
 * }}
 */
export function getTouchpointSendSchedule({
  messageText,
  isHolidayMessage = null,
  sendAfterDate = null,
  sendBeforeDate = null,
  todayStr,
}) {
  const holidayAnchored = resolveIsHolidayMessage(messageText, isHolidayMessage);

  if (!holidayAnchored) {
    return {
      canSendToday: true,
      phase: 'full_month',
      quotaSendAfter: null,
      quotaSendBefore: null,
      quotaLockToFullWindow: false,
      usesHolidayWindow: false,
    };
  }

  if (sendAfterDate && todayStr < sendAfterDate) {
    return {
      canSendToday: false,
      phase: 'waiting',
      quotaSendAfter: sendAfterDate,
      quotaSendBefore: sendBeforeDate,
      quotaLockToFullWindow: true,
      usesHolidayWindow: true,
    };
  }

  if (sendBeforeDate && todayStr > sendBeforeDate) {
    return {
      canSendToday: false,
      phase: 'window_closed',
      quotaSendAfter: sendAfterDate,
      quotaSendBefore: sendBeforeDate,
      quotaLockToFullWindow: true,
      usesHolidayWindow: true,
    };
  }

  return {
    canSendToday: true,
    phase: 'holiday_window',
    quotaSendAfter: sendAfterDate,
    quotaSendBefore: sendBeforeDate,
    quotaLockToFullWindow: true,
    usesHolidayWindow: true,
  };
}
